import { PureComponent } from 'react';
import { styled } from '@linaria/react';

const getDeviceType = () => {
  if (typeof window === 'undefined') {
    return 'desktop';
  }

  if (window.innerWidth < 500) {
    return 'mobile';
  } else {
    return 'desktop';
  }
};

class Bezier extends PureComponent {
  state = {
    draggingPointId: null,
  };

  static defaultProps = {
    viewBoxWidth: 1000,
    viewBoxHeight: 250,
    strokeColor: 'var(--color-secondary)',
    strokeWidth: 6,
    grabbable: true,
  };

  handleSelectPoint = (pointId) => () => {
    if (this.props.grabbable) {
      this.setState({ draggingPointId: pointId });
    }
  };

  handleRelease = () => {
    this.setState({ draggingPointId: null });
  };

  handleDrag = (ev) => {
    // This event handles both mouseMove and touchMove.
    let x, y;
    if (ev.touches) {
      ev.preventDefault();
      const touch = ev.touches[0];
      [x, y] = [touch.clientX, touch.clientY];
    } else {
      [x, y] = [ev.clientX, ev.clientY];
    }

    const { viewBoxWidth, viewBoxHeight, updatePoint, grabbable } =
      this.props;
    const { draggingPointId } = this.state;

    if (!draggingPointId || !grabbable || !updatePoint) {
      return;
    }

    const svgBB = this.node.getBoundingClientRect();
    const positionRelativeToSvg = [x - svgBB.left, y - svgBB.top];

    const positionWithinViewBox = [
      (positionRelativeToSvg[0] * viewBoxWidth) / svgBB.width,
      (positionRelativeToSvg[1] * viewBoxHeight) / svgBB.height,
    ];

    updatePoint(draggingPointId, positionWithinViewBox);
  };

  render() {
    const {
      points,
      viewBoxWidth,
      viewBoxHeight,
      strokeColor,
      strokeWidth,
      grabbable,
    } = this.props;
    const [p1, p2, p3, p4] = points;

    const curveType =
      typeof p4 !== 'undefined' ? 'cubic' : 'quadratic';

    const instructions =
      curveType === 'cubic'
        ? `
            M ${p1[0]},${p1[1]}
            C ${p2[0]},${p2[1]} ${p3[0]},${p3[1]} ${p4[0]},${p4[1]}
          `
        : `
            M ${p1[0]},${p1[1]}
            Q ${p2[0]},${p2[1]} ${p3[0]},${p3[1]}
          `;

    const lastPoint = curveType === 'cubic' ? p4 : p3;
    const lastPointId = curveType === 'cubic' ? 'p4' : 'p3';

    const isMobile = getDeviceType() === 'mobile';

    return (
      <Svg
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        ref={(node) => (this.node = node)}
        onMouseMove={this.handleDrag}
        onTouchMove={this.handleDrag}
        onMouseUp={this.handleRelease}
        onTouchEnd={this.handleRelease}
      >
        <ControlLine x1={p1[0]} y1={p1[1]} x2={p2[0]} y2={p2[1]} />
        {curveType === 'quadratic' && (
          <ControlLine x1={p2[0]} y1={p2[1]} x2={p3[0]} y2={p3[1]} />
        )}
        {curveType === 'cubic' && (
          <ControlLine x1={p3[0]} y1={p3[1]} x2={p4[0]} y2={p4[1]} />
        )}

        <path
          d={instructions}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />

        <EndPoint
          cx={p1[0]}
          cy={p1[1]}
          onMouseDown={this.handleSelectPoint('p1')}
          onTouchStart={this.handleSelectPoint('p1')}
          data-grabbable={String(grabbable)}
          rx={isMobile ? 40 : 15}
          ry={isMobile ? 40 : 15}
        />

        <ControlPoint
          cx={p2[0]}
          cy={p2[1]}
          onMouseDown={this.handleSelectPoint('p2')}
          onTouchStart={this.handleSelectPoint('p2')}
          data-grabbable={String(grabbable)}
          isMobile={isMobile}
        />

        {curveType === 'cubic' && (
          <ControlPoint
            cx={p3[0]}
            cy={p3[1]}
            onMouseDown={this.handleSelectPoint('p3')}
            onTouchStart={this.handleSelectPoint('p3')}
            data-grabbable={String(grabbable)}
            isMobile={isMobile}
          />
        )}

        <EndPoint
          cx={lastPoint[0]}
          cy={lastPoint[1]}
          onMouseDown={this.handleSelectPoint(lastPointId)}
          onTouchStart={this.handleSelectPoint(lastPointId)}
          data-grabbable={String(grabbable)}
          rx={isMobile ? 40 : 15}
          ry={isMobile ? 40 : 15}
        />
      </Svg>
    );
  }
}

const ControlPoint = ({
  cx,
  cy,
  onMouseDown,
  onTouchStart,
  isMobile,
  ...delegated
}) => (
  <g>
    <VisibleControlPoint
      cx={cx}
      cy={cy}
      rx={isMobile ? 20 : 8}
      ry={isMobile ? 20 : 8}
      {...delegated}
    />
    <InvisibleHandle
      cx={cx}
      cy={cy}
      rx={isMobile ? 40 : 25}
      ry={isMobile ? 40 : 25}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      {...delegated}
    />
  </g>
);

const Svg = styled.svg`
  position: relative;
  overflow: visible;
  touch-action: none;
`;

const Point = styled.ellipse`
  cursor: not-allowed;

  &[data-grabbable='true'] {
    cursor: -webkit-grab;

    &:active {
      cursor: -webkit-grabbing;
    }
  }
`;

const EndPoint = styled(Point)`
  fill: var(--color-secondary);

  &[data-grabbable='true'] {
    fill: var(--color-primary);
  }
`;

const VisibleControlPoint = styled(Point)`
  fill: var(--color-background);
  stroke: var(--color-secondary);
  stroke-width: 3;

  &[data-grabbable='true'] {
    stroke: var(--color-primary);
  }
`;

const InvisibleHandle = styled(Point)`
  fill: transparent;
  stroke: transparent;
`;

const ControlLine = styled.line`
  stroke: var(--color-gray-300);
  stroke-dasharray: 5, 5;
  stroke-width: 2;
`;

export default Bezier;
